import React, { useState, useCallback, useEffect } from 'react';
import GameBoard from './GameBoard';
import GameSettings from './GameSettings';
import GameStats from './GameStats';
import GameResult from './GameResult';
import { useGameLogic } from '../hooks/useGameLogic';
import './TicTacToeGame.css';

const TicTacToeGame = () => {
  const [gameSettings, setGameSettings] = useState({
    playerSymbol: null,
    difficulty: null,
    gameStarted: false
  });

  const [stats, setStats] = useState({
    wins: 0,
    losses: 0,
    draws: 0,
    gamesPlayed: 0
  });

  const {
    board,
    currentPlayer,
    gameStatus,
    winner,
    winningLine,
    makeMove,
    resetGame,
    initializeGame
  } = useGameLogic(gameSettings.difficulty, gameSettings.playerSymbol);

  // Update stats when game ends
  useEffect(() => {
    if (gameStatus === 'finished' && winner !== null) {
      setStats(prevStats => {
        const newStats = { ...prevStats };
        newStats.gamesPlayed += 1;

        if (winner === 'draw') {
          newStats.draws += 1;
        } else if (winner === gameSettings.playerSymbol) {
          newStats.wins += 1;
        } else {
          newStats.losses += 1;
        }

        // Save to localStorage
        localStorage.setItem('tic-tac-toe-stats', JSON.stringify(newStats));
        return newStats;
      });
    }
  }, [gameStatus, winner, gameSettings.playerSymbol]);

  // Load stats from localStorage on mount
  useEffect(() => {
    const savedStats = localStorage.getItem('tic-tac-toe-stats');
    if (savedStats) {
      setStats(JSON.parse(savedStats));
    }
  }, []);

  // Initialize game when settings are properly set
  useEffect(() => {
    if (gameSettings.gameStarted && gameSettings.playerSymbol && gameSettings.difficulty) {
      initializeGame();
    }
  }, [gameSettings.gameStarted, gameSettings.playerSymbol, gameSettings.difficulty, initializeGame]);

  const handleStartGame = useCallback((playerSymbol, difficulty) => {
    setGameSettings({
      playerSymbol,
      difficulty,
      gameStarted: true
    });
  }, []);

  const handleNewGame = useCallback(() => {
    resetGame();
  }, [resetGame]);

  const handleChangeSettings = useCallback(() => {
    setGameSettings({
      playerSymbol: null,
      difficulty: null,
      gameStarted: false
    });
    resetGame();
  }, [resetGame]);

  const handleSquareClick = useCallback((index) => {
    if (gameStatus === 'playing' && board[index] === null && currentPlayer === gameSettings.playerSymbol) {
      makeMove(index);
    }
  }, [board, currentPlayer, gameSettings.playerSymbol, gameStatus, makeMove]);

  return (
    <div className="tic-tac-toe-game">
      <div className="game-container">
        <h1 className="game-title">Tic-Tac-Toe</h1>

        {!gameSettings.gameStarted ? (
          <GameSettings onStartGame={handleStartGame} />
        ) : (
          <>
            <div className="game-info">
              <div className="player-info">
                <span>You: {gameSettings.playerSymbol}</span>
                <span>Computer: {gameSettings.playerSymbol === 'X' ? 'O' : 'X'}</span>
                <span>Difficulty: {gameSettings.difficulty}</span>
              </div>
              {gameStatus === 'playing' && (
                <div className="current-turn">
                  {currentPlayer === gameSettings.playerSymbol ? "Your turn" : "Computer's turn"}
                </div>
              )}
            </div>

            <div className="game-content">
              <div className="game-board-section">
                <GameBoard
                  board={board}
                  onSquareClick={handleSquareClick}
                  winningLine={winningLine}
                  isPlayerTurn={currentPlayer === gameSettings.playerSymbol}
                />
              </div>

              <div className="game-stats-section">
                <GameStats stats={stats} />
              </div>
            </div>

            {gameStatus === 'finished' && (
              <GameResult
                winner={winner}
                playerSymbol={gameSettings.playerSymbol}
                onNewGame={handleNewGame}
                onChangeSettings={handleChangeSettings}
              />
            )}
          </>
        )}
      </div>
    </div>
  );
};

export default TicTacToeGame;